/**
 * Ledger backend: where the books live, how the chart is mapped, and whether
 * anything is stuck.
 *
 * The reconciliation screen answers one question and puts it at the top in
 * words rather than numbers: *is everything we posted actually in the
 * accounting system?* A controller should not have to read four counters and
 * do the subtraction themselves.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date, dateTime, titleCase } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';

interface LedgerConfig {
  backend_type: 'native' | 'quickbooks';
  sync_interval_minutes: number;
  onboarding_type: 'self_service' | 'white_glove';
  summarization: 'per_entry' | 'daily_summary';
  is_paused: boolean;
  connection_status: 'not_connected' | 'connected' | 'expired' | 'revoked';
  external_realm_id: string | null;
  last_sync_completed_at: string | null;
}

interface SyncStatus {
  backendType: string;
  connectionStatus: string;
  isPaused: boolean;
  syncIntervalMinutes: number;
  pending: number;
  syncing: number;
  failed: number;
  synced: number;
  oldestUnsyncedAt: string | null;
  unsyncedValueMinor: number;
  lastSyncCompletedAt: string | null;
  behind: boolean;
}

interface MappingRow {
  internalCategory: string;
  label: string;
  requirement: 'platform' | 'club_revenue';
  status: 'mapped' | 'unverified' | 'stale' | 'missing';
  targetAccountRef: string | null;
  targetAccountName: string | null;
  lastVerifiedAt: string | null;
}

interface MappingStatus {
  backendType: 'native' | 'quickbooks';
  complete: boolean;
  missing: number;
  rows: MappingRow[];
}

interface SyncException {
  ledger_transaction_id: string;
  external_ref: string;
  txn_type: string;
  source_module: string;
  txn_date: string;
  status: string;
  attempt_count: number;
  next_attempt_at: string | null;
  last_error: string | null;
  entry_number: number | null;
  memo: string | null;
  entry_value_minor: number;
  age_seconds: number;
}

function humanAge(seconds: number): string {
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} hr`;
  return `${Math.round(hours / 24)} days`;
}

/* ================================================================== */
export function LedgerSyncPage() {
  const { currency, atLeast } = useSession();
  const status = useApi<SyncStatus>('/v1/ledger/sync');
  const exceptions = useApi<{ exceptions: SyncException[] }>('/v1/ledger/sync/exceptions');
  const runs = useApi<{ runs: Record<string, unknown>[] }>('/v1/ledger/sync/runs');

  const syncNow = useMutation(async () => api('/v1/ledger/sync/run', { method: 'POST' }));
  const requeue = useMutation(async (id: string) =>
    api(`/v1/ledger/transactions/${id}/requeue`, { method: 'POST' }),
  );

  const reloadAll = () => {
    status.reload();
    exceptions.reload();
    runs.reload();
  };

  if (status.loading && !status.data) return <Loading rows={6} />;
  if (status.error) return <ErrorBanner error={status.error} />;
  const s = status.data;
  if (!s) return null;

  const native = s.backendType === 'native';
  const stuck = s.pending + s.failed;

  // The headline. Everything below is the detail behind this one sentence.
  const verdict = native
    ? { tone: 'good' as const, text: 'This club keeps its books in BetterClub. Nothing to export.' }
    : s.isPaused
      ? { tone: 'warning' as const, text: 'Sync is paused. Entries are still being recorded and will go across when you resume.' }
      : s.connectionStatus !== 'connected'
        ? { tone: 'critical' as const, text: `The QuickBooks connection is ${s.connectionStatus}. Nothing is reaching QuickBooks.` }
        : s.failed > 0
          ? { tone: 'critical' as const, text: `${s.failed} ${s.failed === 1 ? 'entry has' : 'entries have'} failed to reach QuickBooks.` }
          : stuck === 0
            ? { tone: 'good' as const, text: 'Everything posted in BetterClub is in QuickBooks.' }
            : s.behind
              ? { tone: 'serious' as const, text: `${stuck} ${stuck === 1 ? 'entry has' : 'entries have'} been waiting longer than expected.` }
              : { tone: 'neutral' as const, text: `${stuck} ${stuck === 1 ? 'entry is' : 'entries are'} queued for the next sync.` };

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Accounting sync</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Member balances, statements and the trial balance are kept here whatever this says. What
        it governs is whether the resulting journal is also sent to an outside accounting system.
      </p>

      <Card
        title="Where Things Stand"
        actions={
          !native &&
          atLeast('controller') && (
            <button className="small" disabled={syncNow.busy} onClick={() => syncNow.run().then(reloadAll)}>
              {syncNow.busy ? 'Syncing…' : 'Sync now'}
            </button>
          )
        }
      >
        <ErrorBanner error={syncNow.error} />

        <p style={{ margin: '0 0 16px', fontSize: 15 }}>
          <Status tone={verdict.tone}>{verdict.text}</Status>
        </p>

        <div className="tiles">
          <Tile label="Books kept in" value={native ? 'BetterClub' : 'QuickBooks Online'} />
          {!native && (
            <>
              <Tile
                label="Waiting to go across"
                value={String(stuck)}
                note={stuck > 0 ? <Money minor={s.unsyncedValueMinor} currency={currency} /> : 'nothing queued'}
              />
              <Tile
                label="Oldest waiting"
                value={s.oldestUnsyncedAt ? humanAge(Math.round((Date.now() - Date.parse(s.oldestUnsyncedAt)) / 1000)) : '—'}
                note={`syncs every ${s.syncIntervalMinutes} min`}
                tone={s.behind ? 'serious' : undefined}
              />
              <Tile
                label="Last reached QuickBooks"
                value={s.lastSyncCompletedAt ? dateTime(s.lastSyncCompletedAt) : 'never'}
              />
            </>
          )}
        </div>
      </Card>

      {!native && (
        <Card title="Posted in BetterClub, Not yet in QuickBooks" flush>
          {exceptions.loading && !exceptions.data ? (
            <div style={{ padding: 16 }}>
              <Loading rows={3} />
            </div>
          ) : (exceptions.data?.exceptions.length ?? 0) === 0 ? (
            <Empty>Nothing outstanding. Every posted entry has been accepted by QuickBooks.</Empty>
          ) : (
            <table className="table">
              <colgroup>
                <col style={{ width: '11%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '9%' }} />
                <col />
                <col style={{ width: '9%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Entry</th>
                  <th>Source</th>
                  <th className="num">Value</th>
                  <th style={{ paddingLeft: 18 }}>Waiting</th>
                  <th>Why it is stuck</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {exceptions.data!.exceptions.map((e) => (
                  <tr key={e.ledger_transaction_id}>
                    <td>{date(e.txn_date)}</td>
                    <td>
                      <code>{e.entry_number ?? '—'}</code>
                      <div className="muted small">{e.memo}</div>
                    </td>
                    <td>{titleCase(e.source_module)}</td>
                    <td className="num">
                      <Money minor={e.entry_value_minor} currency={currency} />
                    </td>
                    <td style={{ paddingLeft: 18 }}>{humanAge(e.age_seconds)}</td>
                    <td style={{ maxWidth: 320 }}>
                      {e.status === 'failed' ? (
                        <>
                          <Status tone="critical">attempt {e.attempt_count}</Status>
                          <div className="muted small">{e.last_error}</div>
                          {e.next_attempt_at && (
                            <div className="muted small">retries {dateTime(e.next_attempt_at)}</div>
                          )}
                        </>
                      ) : e.status === 'syncing' ? (
                        <Status tone="neutral">going across now</Status>
                      ) : (
                        <Status tone="neutral">queued</Status>
                      )}
                    </td>
                    <td className="num">
                      {e.status === 'failed' && atLeast('controller') && (
                        <button
                          className="secondary small"
                          disabled={requeue.busy}
                          onClick={() => requeue.run(e.ledger_transaction_id).then(reloadAll)}
                        >
                          Try again
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {!native && (
        <Card title="Recent Sync Runs" flush>
          {(runs.data?.runs.length ?? 0) === 0 ? (
            <Empty>No sync has run yet.</Empty>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Result</th>
                  <th className="num">Attempted</th>
                  <th className="num">Accepted</th>
                  <th className="num">Failed</th>
                </tr>
              </thead>
              <tbody>
                {runs.data!.runs.map((r) => (
                  <tr key={String(r.id)}>
                    <td>{dateTime(String(r.started_at))}</td>
                    <td>
                      <Status tone={r.status === 'completed' ? 'good' : r.status === 'failed' ? 'critical' : 'neutral'}>
                        {String(r.status)}
                      </Status>
                    </td>
                    <td className="num">{String(r.attempted_count)}</td>
                    <td className="num">{String(r.synced_count)}</td>
                    <td className="num">{String(r.failed_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}

/* ================================================================== */
export function LedgerSettingsPage() {
  const { atLeast } = useSession();
  const config = useApi<LedgerConfig>('/v1/ledger/config');
  const save = useMutation(async (body: Partial<LedgerConfig>) =>
    api('/v1/ledger/config', { method: 'PUT', body }),
  );

  const [draft, setDraft] = useState<Partial<LedgerConfig>>({});
  const c = config.data;
  const value = <K extends keyof LedgerConfig>(k: K): LedgerConfig[K] | undefined =>
    (draft[k] ?? c?.[k]) as LedgerConfig[K] | undefined;

  if (config.loading && !c) return <Loading rows={5} />;
  if (!c) return <ErrorBanner error={config.error} />;

  const editable = atLeast('controller');
  const external = value('backend_type') !== 'native';

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Ledger Settings</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Choose where this club's books are kept, and how often entries are sent across.
      </p>

      <Card title="Where This Club's Books Live">
        <ErrorBanner error={save.error} />
        {save.error?.code === 'ledger_mapping_incomplete' && (
          <div className="error-banner">
            <strong>Map these before switching:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {((save.error.detail as { missing?: string[] })?.missing ?? []).map((m) => (
                <li key={m}>
                  <code>{m}</code>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="muted" style={{ marginTop: 0 }}>
          Member accounts, statements, minimums and the trial balance always live in BetterClub.
          What this setting changes is whether the resulting journal is also sent to an outside
          accounting system, and how often.
        </p>

        <div style={{ maxWidth: 560 }}>
          <label className="field">
            <span>Accounting system</span>
            <select
              disabled={!editable}
              value={value('backend_type')}
              onChange={(e) => setDraft({ ...draft, backend_type: e.target.value as LedgerConfig['backend_type'] })}
            >
              <option value="native">BetterClub — keep the books here</option>
              <option value="quickbooks">QuickBooks Online — send journals across</option>
            </select>
          </label>

          {external && (
            <>
              <label className="field">
                <span>QuickBooks company (realm) ID</span>
                <input
                  disabled={!editable}
                  value={value('external_realm_id') ?? ''}
                  onChange={(e) => setDraft({ ...draft, external_realm_id: e.target.value || null })}
                />
              </label>

              <label className="field">
                <span>Connection</span>
                <select
                  disabled={!editable}
                  value={value('connection_status')}
                  onChange={(e) =>
                    setDraft({ ...draft, connection_status: e.target.value as LedgerConfig['connection_status'] })
                  }
                >
                  <option value="not_connected">not connected</option>
                  <option value="connected">connected</option>
                  <option value="expired">expired</option>
                  <option value="revoked">revoked</option>
                </select>
              </label>

              <label className="field">
                <span>Send entries every</span>
                <select
                  disabled={!editable}
                  value={String(value('sync_interval_minutes'))}
                  onChange={(e) => setDraft({ ...draft, sync_interval_minutes: Number(e.target.value) })}
                >
                  <option value="5">5 minutes — premium</option>
                  <option value="15">15 minutes — premium</option>
                  <option value="60">hour — standard</option>
                  <option value="240">4 hours</option>
                  <option value="1440">day — end of day</option>
                </select>
              </label>

              <label className="field">
                <span>Level of detail</span>
                <select
                  disabled={!editable}
                  value={value('summarization')}
                  onChange={(e) =>
                    setDraft({ ...draft, summarization: e.target.value as LedgerConfig['summarization'] })
                  }
                >
                  <option value="per_entry">One journal per entry</option>
                  <option value="daily_summary">One summary journal per day</option>
                </select>
              </label>

              <label className="row" style={{ marginBottom: 13, gap: 8 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto', flex: '0 0 auto' }}
                  disabled={!editable}
                  checked={Boolean(value('is_paused'))}
                  onChange={(e) => setDraft({ ...draft, is_paused: e.target.checked })}
                />
                <span>Pause sending (entries keep being recorded and queue up)</span>
              </label>
            </>
          )}
        </div>

        {editable && (
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button
              disabled={save.busy || Object.keys(draft).length === 0}
              onClick={() => save.run(draft).then((r) => r && (setDraft({}), config.reload()))}
            >
              {save.busy ? 'Saving…' : 'Save'}
            </button>
            {Object.keys(draft).length > 0 && (
              <button className="secondary" onClick={() => setDraft({})}>
                Discard
              </button>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

/* ================================================================== */
export function LedgerMappingPage() {
  const { atLeast } = useSession();
  const mapping = useApi<MappingStatus>('/v1/ledger/mapping');
  const accounts = useApi<{ accounts: { id: string; account_number: string; name: string }[] }>(
    '/v1/ledger/mapping/accounts',
  );
  const save = useMutation(
    async (body: { backend_type: string; mappings: Record<string, string>[] }) =>
      api('/v1/ledger/mapping', { method: 'PUT', body }),
  );

  const [edits, setEdits] = useState<Record<string, string>>({});
  const m = mapping.data;

  const grouped = useMemo(() => {
    const rows = m?.rows ?? [];
    return {
      missing: rows.filter((r) => r.status === 'missing'),
      rest: rows.filter((r) => r.status !== 'missing'),
    };
  }, [m]);

  if (mapping.loading && !m) return <Loading rows={6} />;
  if (!m) return <ErrorBanner error={mapping.error} />;

  const editable = atLeast('controller');
  const native = m.backendType === 'native';

  const commit = async () => {
    const mappings = Object.entries(edits)
      .filter(([, ref]) => ref.trim() !== '')
      .map(([internal_category, target_account_ref]) => ({
        internal_category,
        target_account_ref: target_account_ref.trim(),
      }));
    if (mappings.length === 0) return;
    const result = await save.run({ backend_type: m.backendType, mappings });
    if (result) {
      setEdits({});
      mapping.reload();
    }
  };

  const row = (r: MappingRow) => (
    <tr key={r.internalCategory}>
      <td>
        <strong>{r.label}</strong>
        <div className="muted small">
          <code>{r.internalCategory}</code>
          {r.requirement === 'club_revenue' && ' · your own revenue category'}
        </div>
      </td>
      <td>
        {r.status === 'missing' ? (
          <Status tone="critical">not mapped</Status>
        ) : r.status === 'stale' ? (
          <Status tone="warning">not checked since {date(r.lastVerifiedAt)}</Status>
        ) : r.status === 'unverified' ? (
          <Status tone="neutral">never confirmed</Status>
        ) : (
          <Status tone="good">mapped</Status>
        )}
      </td>
      <td>
        {editable ? (
          native ? (
            <select
              value={edits[r.internalCategory] ?? r.targetAccountRef ?? ''}
              onChange={(e) => setEdits({ ...edits, [r.internalCategory]: e.target.value })}
            >
              <option value="">— choose an account —</option>
              {(accounts.data?.accounts ?? []).map((a) => (
                <option key={a.id} value={a.account_number}>
                  {a.account_number} · {a.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              placeholder="QuickBooks account ID"
              value={edits[r.internalCategory] ?? r.targetAccountRef ?? ''}
              onChange={(e) => setEdits({ ...edits, [r.internalCategory]: e.target.value })}
            />
          )
        ) : (
          <span>
            {r.targetAccountRef ?? '—'}
            {r.targetAccountName && <span className="muted"> · {r.targetAccountName}</span>}
          </span>
        )}
      </td>
    </tr>
  );

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Account Mapping</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Where each kind of money BetterClub records lands in{' '}
        {native ? "this club's chart of accounts" : 'QuickBooks'}.
      </p>

      <Card
        title="Ready to Sync?"
        actions={
          editable && (
            <button className="small" disabled={save.busy || Object.keys(edits).length === 0} onClick={commit}>
              {save.busy ? 'Saving…' : `Save ${Object.keys(edits).length || ''}`.trim()}
            </button>
          )
        }
      >
        <ErrorBanner error={save.error} />

        <p className="muted" style={{ marginTop: 0 }}>
          Every category BetterClub can post to needs somewhere to land. Your own revenue
          categories appear here automatically — add one in the chart of accounts and it shows up
          as something to map, rather than turning up unmapped at month end.
        </p>

        {m.complete ? (
          <p>
            <Status tone="good">Everything is mapped. This club is ready to sync.</Status>
          </p>
        ) : (
          <p>
            <Status tone="critical">
              {m.missing} {m.missing === 1 ? 'category has' : 'categories have'} nowhere to post.
              Nothing will sync until they do.
            </Status>
          </p>
        )}
      </Card>

      {grouped.missing.length > 0 && (
        <Card title="Needs Mapping" flush>
          <table className="table">
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '22%' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Category</th>
                <th>Status</th>
                <th>Posts to</th>
              </tr>
            </thead>
            <tbody>{grouped.missing.map(row)}</tbody>
          </table>
        </Card>
      )}

      <Card title="Mapped" flush>
        {grouped.rest.length === 0 ? (
          <Empty>Nothing mapped yet.</Empty>
        ) : (
          <table className="table">
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '22%' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Category</th>
                <th>Status</th>
                <th>Posts to</th>
              </tr>
            </thead>
            <tbody>{grouped.rest.map(row)}</tbody>
          </table>
        )}
      </Card>
    </>
  );
}
